import type { TeamData } from '../Cteamcard';
import type { TeamPreviewData } from './Chero.types';

type TeamModelIds = NonNullable<TeamPreviewData['modelIds']>;

interface BuildTeamPreviewMapInput {
  teams: TeamPreviewData[];
}

interface SelectHeroTeamsDataInput {
  teamIds: string[];
  teamById: Map<string, TeamPreviewData>;
}

interface TeamPreviewInput {
  team: TeamPreviewData;
}

interface OptionalTeamCatchPhraseInput {
  catchPhrase: TeamPreviewData['catchPhrase'];
}

interface OptionalTeamCategoryInput {
  category: TeamPreviewData['category'];
}

const EMPTY_MODEL_IDS: TeamModelIds = [];

export function buildTeamPreviewMap({
  teams,
}: BuildTeamPreviewMapInput): Map<string, TeamPreviewData> {
  return new Map(teams.map((team) => [team.id, team]));
}

function getTeamModelIds({ team }: TeamPreviewInput): TeamModelIds {
  return team.modelIds ?? EMPTY_MODEL_IDS;
}

function getOptionalTeamCatchPhrase({
  catchPhrase,
}: OptionalTeamCatchPhraseInput): TeamData['catchPhrase'] {
  return catchPhrase ?? undefined;
}

function getOptionalTeamCategory({
  category,
}: OptionalTeamCategoryInput): TeamData['category'] {
  return category ?? undefined;
}

function toHeroTeamData({ team }: TeamPreviewInput): TeamData {
  const modelIds = getTeamModelIds({ team });

  return {
    id: team.id,
    name: team.name,
    memberCount: modelIds.length,
    models: modelIds.map((modelId) => String(modelId)),
    catchPhrase: getOptionalTeamCatchPhrase({
      catchPhrase: team.catchPhrase,
    }),
    category: getOptionalTeamCategory({ category: team.category }),
  };
}

export function selectHeroTeamsData({
  teamIds,
  teamById,
}: SelectHeroTeamsDataInput): TeamData[] {
  return teamIds
    .map((teamId) => teamById.get(teamId))
    .filter((team): team is TeamPreviewData => team != null)
    .map((team) => toHeroTeamData({ team }));
}
